import { patientApi } from '@/api/PatientApi'
import type {
	PatientBillingPrimaryCard,
	PatientBillingRecord,
} from '@/api/types/PatientApi'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { PAYMENT_PLANS_QUERY_KEY } from './usePaymentPlans'

/**
 * Show the new default card immediately from the write-through response, then refetch as a
 * backstop so the record stays consistent with the server.
 */
const applyPrimaryCard = (
	queryClient: QueryClient,
	primaryCard: PatientBillingPrimaryCard | null
) => {
	if (primaryCard) {
		queryClient.setQueryData(['patient-billing'], (old?: PatientBillingRecord) =>
			old ? { ...old, primaryCard } : old
		)
	}
	queryClient.invalidateQueries({ queryKey: ['patient-billing'] })
	queryClient.invalidateQueries({ queryKey: PAYMENT_PLANS_QUERY_KEY })
}

const useUpdatePrimaryCard = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token }: { token: string }) => patientApi.updatePrimaryCard({ token }),
		onSuccess: (primaryCard: PatientBillingPrimaryCard | null) =>
			applyPrimaryCard(queryClient, primaryCard),
	})
}

/** The second half of a Stripe card setup: the confirmed intent's card becomes the default. */
const useCompleteCardSetup = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ setupIntentId }: { setupIntentId: string }) =>
			patientApi.completeCardSetup({ setupIntentId }),
		onSuccess: (primaryCard) => applyPrimaryCard(queryClient, primaryCard),
	})
}

export { useUpdatePrimaryCard, useCompleteCardSetup }
